import React, { useRef } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { NavButton } from "../Buttons/NavButton";
import { SmallTextGradient, IconGradient } from "../Gradient";
import { getGlassCardStyles } from "../../utility/glassCard.utils";

type NavItem = { label: string; path: string; key: string };

type NavLinksProps = {
  active: string | null;
  setActive: (key: string) => void;
  navItems: NavItem[];
  handleCreateMenuOpen: (e: React.MouseEvent<HTMLElement>) => void;
  moreMenuAnchor: HTMLElement | null;
  moreOpen: boolean;
  handleMoreOpen: (e: React.MouseEvent<HTMLElement>) => void;
  handleMoreClose: () => void;
  onExploreClick?: (e: React.MouseEvent<HTMLElement>) => void;
  exploreButtonRef?: React.RefObject<HTMLButtonElement>;
  exploreMenuOpen?: boolean;
  onChallengesClick?: (e: React.MouseEvent<HTMLElement>) => void;
  challengesButtonRef?: React.RefObject<HTMLButtonElement>;
  challengesMenuOpen?: boolean;
};

const NavLinks: React.FC<NavLinksProps> = ({
  active,
  setActive,
  navItems,
  handleCreateMenuOpen,
  moreMenuAnchor,
  moreOpen,
  handleMoreOpen,
  handleMoreClose,
  onExploreClick,
  exploreButtonRef,
  exploreMenuOpen,
  onChallengesClick,
  challengesButtonRef,
  challengesMenuOpen,
}) => {
  const writeButtonRef = useRef<HTMLButtonElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      {navItems.map((item) => {
        const isActive = active === item.key;
        // Handle Explore link specially - show dropdown menu like Write and More
        if (item.key === "explore" && onExploreClick) {
          return (
            <Box key={item.key} sx={{ position: "relative" }}>
              <NavButton
                ref={exploreButtonRef}
                onClick={onExploreClick}
                disableRipple
                active={isActive || exploreMenuOpen || false}
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    display: "flex !important",
                  },
                  "& .arrow-icon": {
                    flexShrink: 0,
                    alignSelf: "center",
                    "& svg": {
                      fontSize: 18,
                    },
                  },
                  "&:hover .arrow-icon": {
                    alignSelf: "center",
                    top: 0,
                    "& svg": {
                      backgroundImage: (theme) =>
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)"
                          : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",
                      fill: "transparent",
                      transform: "none",
                      position: "relative",
                      top: 0,
                    },
                  },
                }}
                aria-haspopup="true"
                aria-expanded={exploreMenuOpen ? "true" : undefined}
              >
                {item.label}
                <Box
                  component="span"
                  sx={(theme) => ({
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 0.5,
                    flexShrink: 0,
                    alignSelf: "center",
                    position: "relative",
                    isolation: "isolate",
                    height: "1em",
                    lineHeight: 1,
                    top: 0,
                    color: `${theme.palette.text.primary} !important`,
                    WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                    "& svg": {
                      fontSize: 18,
                      width: "1em",
                      height: "1em",
                      color: `${theme.palette.text.primary} !important`,
                      fill: `${theme.palette.text.primary} !important`,
                      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                      transition: "color 0.3s ease, fill 0.3s ease, background-image 0.3s ease",
                      display: "block",
                      margin: 0,
                      padding: 0,
                      position: "relative",
                      top: 0,
                    },
                  })}
                  className="arrow-icon"
                >
                  <ArrowDropDownIcon />
                </Box>
              </NavButton>
            </Box>
          );
        }
        return (
          <Box key={item.key} sx={{ position: "relative" }}>
            <NavButton
              component={Link}
              to={item.path}
              onClick={() => setActive(item.key)}
              disableRipple
              active={isActive}
              sx={{ fontWeight: 600 }}
            >
              {item.label}
            </NavButton>
          </Box>
        );
      })}
      <Box sx={{ position: "relative" }}>
        <NavButton
          ref={challengesButtonRef}
          onClick={onChallengesClick}
          disableRipple
          active={active === "ink-masters" || challengesMenuOpen || false}
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            "&:hover": {
              display: "flex !important",
            },
            "& .arrow-icon": {
              flexShrink: 0,
              alignSelf: "center",
              "& svg": {
                fontSize: 18,
              },
            },
            "&:hover .arrow-icon": {
              alignSelf: "center",
              top: 0,
              "& svg": {
                backgroundImage: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                fill: "transparent",
                transform: "none",
                position: "relative",
                top: 0,
              },
            },
          }}
          aria-haspopup="true"
          aria-expanded={challengesMenuOpen ? "true" : undefined}
        >
          Challenges
          <Box
            component="span"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 0.5,
              flexShrink: 0,
              alignSelf: "center",
              position: "relative",
              isolation: "isolate",
              height: "1em",
              lineHeight: 1,
              top: 0,
              color: `${theme.palette.text.primary} !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              "& svg": {
                fontSize: 18,
                width: "1em",
                height: "1em",
                color: `${theme.palette.text.primary} !important`,
                fill: `${theme.palette.text.primary} !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                transition: "color 0.3s ease, fill 0.3s ease, background-image 0.3s ease",
                display: "block",
                margin: 0,
                padding: 0,
                position: "relative",
                top: 0,
              },
            })}
            className="arrow-icon"
          >
            <ArrowDropDownIcon />
          </Box>
        </NavButton>
      </Box>
      <Box sx={{ position: "relative" }}>
        <NavButton
          ref={writeButtonRef}
          onClick={handleCreateMenuOpen}
          disableRipple
          active={active === "create" || active === "create-story"}
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            "&:hover": {
              display: "flex !important", // Override inline-block from applyGradientText
            },
            "& .arrow-icon": {
              flexShrink: 0,
              alignSelf: "center",
              "& svg": {
                fontSize: 18,
              },
            },
            "&:hover .arrow-icon": {
              alignSelf: "center",
              top: 0,
              "& svg": {
                backgroundImage: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                fill: "transparent",
                transform: "none",
                position: "relative",
                top: 0,
              },
            },
          }}
        >
          Write
          <Box
            component="span"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 0.5,
              flexShrink: 0,
              alignSelf: "center",
              position: "relative",
              isolation: "isolate",
              height: "1em",
              lineHeight: 1,
              top: 0,
              color: `${theme.palette.text.primary} !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              "& svg": {
                fontSize: 18,
                width: "1em",
                height: "1em",
                color: `${theme.palette.text.primary} !important`,
                fill: `${theme.palette.text.primary} !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                transition: "color 0.3s ease, fill 0.3s ease, background-image 0.3s ease",
                display: "block",
                margin: 0,
                padding: 0,
                position: "relative",
                top: 0,
              },
            })}
            className="arrow-icon"
          >
            <ArrowDropDownIcon />
          </Box>
        </NavButton>
      </Box>
      <Box sx={{ position: "relative" }}>
        <NavButton
          ref={moreButtonRef}
          onClick={handleMoreOpen}
          disableRipple
          active={false}
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            "&:hover": {
              display: "flex !important", // Override inline-block from applyGradientText
            },
            "& .arrow-icon": {
              flexShrink: 0,
              alignSelf: "center",
              "& svg": {
                fontSize: 18,
              },
            },
            "&:hover .arrow-icon": {
              alignSelf: "center",
              top: 0,
              "& svg": {
                backgroundImage: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                fill: "transparent",
                transform: "none",
                position: "relative",
                top: 0,
              },
            },
          }}
          aria-haspopup="true"
          aria-expanded={moreOpen ? "true" : undefined}
          aria-controls={moreOpen ? "more-menu" : undefined}
        >
          More
          <Box
            component="span"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 0.5,
              flexShrink: 0,
              alignSelf: "center",
              position: "relative",
              isolation: "isolate",
              height: "1em",
              lineHeight: 1,
              top: 0,
              color: `${theme.palette.text.primary} !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              "& svg": {
                fontSize: 18,
                width: "1em",
                height: "1em",
                color: `${theme.palette.text.primary} !important`,
                fill: `${theme.palette.text.primary} !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                transition: "color 0.3s ease, fill 0.3s ease, background-image 0.3s ease",
                display: "block",
                margin: 0,
                padding: 0,
                position: "relative",
                top: 0,
              },
            })}
            className="arrow-icon"
          >
            <ArrowDropDownIcon />
          </Box>
        </NavButton>
        <Menu
          id="more-menu"
          anchorEl={moreMenuAnchor}
          open={moreOpen}
          onClose={handleMoreClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            elevation: 0,
            sx: (theme) => ({
              ...getGlassCardStyles(theme),
              mt: 1,
              p: 0,
              width: 'auto',
              borderRadius: 3,
            }),
          }}
        >
          <MenuItem
            component={Link}
            to="/about"
            onClick={() => {
              handleMoreClose();
              setActive("about");
            }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease",
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              position: 'relative',
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                "& .menu-text-wrapper::after": {
                  transform: "scaleX(1)",
                },
              },
              "& .menu-text-wrapper": {
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -2,
                  height: "2px",
                  background: theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                  borderRadius: "1px",
                  transform: "scaleX(0)",
                  transformOrigin: "left center",
                  transition: "transform 0.2s ease",
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <InfoOutlinedIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                About
              </SmallTextGradient>
            </Box>
          </MenuItem>
          <MenuItem
            component={Link}
            to="/contact"
            onClick={() => {
              handleMoreClose();
              setActive("contact");
            }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease",
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              position: 'relative',
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                "& .menu-text-wrapper::after": {
                  transform: "scaleX(1)",
                },
              },
              "& .menu-text-wrapper": {
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -2,
                  height: "2px",
                  background: theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                  borderRadius: "1px",
                  transform: "scaleX(0)",
                  transformOrigin: "left center",
                  transition: "transform 0.2s ease",
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <ContactMailOutlinedIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                Contact
              </SmallTextGradient>
            </Box>
          </MenuItem>
          <MenuItem
            component={Link}
            to="/careers"
            onClick={() => {
              handleMoreClose();
              setActive("careers");
            }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.2s ease",
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              position: 'relative',
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                "& .menu-text-wrapper::after": {
                  transform: "scaleX(1)",
                },
              },
              "& .menu-text-wrapper": {
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -2,
                  height: "2px",
                  background: theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
                    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)",
                  borderRadius: "1px",
                  transform: "scaleX(0)",
                  transformOrigin: "left center",
                  transition: "transform 0.2s ease",
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <WorkOutlineOutlinedIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                Careers
              </SmallTextGradient>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
};

export default NavLinks;


